import Backbone from 'backbone'
import template from './pagination.hbs'
import paginationControl from './pagination-control.hbs'
import _ from 'lodash'
import $ from 'jquery'
import loadingDots from 'scoreboard/components/loading-dots.hbs'
import deviceType from 'scoreboard/components/device-type'

// Values must remain in sync with vars at top of pagination.scss
const digitPadding = 5
const digitWidth = 6
const pageWidth = (2*digitPadding) + digitWidth

var Pagination = Backbone.View.extend({

	className: 'pagination',

	events: {
		'click [data-page-id]': '_onClickPage'
	},

	/**
	 * @param  {Array} options.data The data which will be used to build the paginated table
	 * @param  {Function} options.tbody A reference to the tbody which the rows should be fed into.
	 * @param  {Function} [options.rowTemplate] A template to use when creating the table rows
	 * @param  {Function} [options.row] A function which can be used to manually build each row.
	 * This is quicker than using the rowTemplate option but requires more code so there's the tradeoff.
	 * It should be noted that either rowTamplate or row must be present, if both are present, rowTemplate is used.
	 * @param  {Number} [options.pageSize] How many rows should be present on each page
	 * @param  {Number} [options.page] The page the control should show after initial render
	 * @param  {Function} [options.setupTableBody] A function which can be run after the table body has been rendered
	 * but before the loading dots have been hidden.
	 * @param  {Boolean} [options.stickyHeader=true] True if the table headers should stick to the top
	 */
	initialize(options) {
		this.options = options
		this.stickyHeader = _.get(this.options, 'stickyHeader', true)
		this.data = options.data
		this.pageSize = options.pageSize || 50
		// if this.options.setupTableBody doesn't exist, don't worry, set it here as an
		// empty function so we don't have to constantly check later while building pages.
		if (this.options.setupTableBody) {
			this.setupTableBody = this.options.setupTableBody
		} else {
			this.setupTableBody = () => void 0
		}
	},

	render() {
		this._setupControl()
		this.$el.append(template())
		// store panel/tbody/panelContent/paginationControlWrapper to limit calls to the dom later
		this.panel = this.$('.panel')
		this.tbody = this.$('tbody')
		this.panelContent = this.$('.panel-content')
		this.paginationControlWrapper = this.$('.pagination-control-wrapper')
		this._renderPaginationControl()
		this._renderTableHeader()
		this.$('.panel-content').append(loadingDots())
		this._renderTableBody()
		if (this.stickyHeader) {
			this.$('.panel').addClass('has-sticky-headers')
		}
		this.originaWidth = this.paginationControlWrapper.width() - pageWidth*2 // - arrows
		this.largestNumWidth = this.$(`[data-page-id=${this.pages.length - 1}]`).outerWidth()

		// Once it's rendered, resize it to fit the container
		this.resize()
		this.trigger('ready')
		return this
	},

	_renderPaginationControl() {
		this.paginationControlWrapper.empty()
			.append(paginationControl({
				pages: this.pages
			}))
	},

	_renderTableHeader() {
		this.$('.table thead').append(this.options.headerRowTemplate())
	},

	_selectedHasLeftEllipsisSibling() {
		return this.$('span.is-selected').prev().hasClass('ellipsis')
	},

	_selectedHasRightEllipsisSibling() {
		return this.$('span.is-selected').next().hasClass('ellipsis')
	},

	_selectedHasEllipsisSibling() {
		return this._selectedHasLeftEllipsisSibling() || this._selectedHasRightEllipsisSibling()
	},

	_onClickPage(e) {
		this.setPage(this.$(e.target).closest('span').data('page-id'))
		this._selectedHasEllipsisSibling() && this.resize()
	},

	// Toggles the visibility of the loading dots on the paginated table. The
	// look we're going for here is to have the table be not scrollable and only
	// the header is visible with the loading dots showing up in the center.
	toggleLoadingDots(show) {
		this.$el.toggleClass('is-loading', show)
		// If we're showing the loading dots, make sure the panel keeps its shape,
		// otherwise, let its contents dictate its size.
		this.panel.css({
			'min-height': show ? this.panel.height() : ''
		})

		this.panelContent.scrollTop(0)
	},

	/**
	 * Renders the body of the paginated table
	 */
	_renderTableBody() {
		this.isRenderingTable = true
		// re-render the pagination control so the numbers are updated
		this._renderPaginationControl()
		// Show the loading dots immediately
		this.toggleLoadingDots(true)
		// In order for operations which need to happen on the 'beforePageRendered'
		// event to actually happen before the page starts rendering, we need to
		// put the following inside setTimeout(). This is because the building
		// of the table can be very DOM intensive and that will lock up anything
		// else that needs to happen before this does.
		// setTimeout() was chosen specifically in order to accommodate chrome/ff/safari.
		setTimeout(() => {
			// clear the pagination controls
			this.paginationControlWrapper.empty()

			// clear the rows from the table body
			this.tbody.empty()

			// render and append new rows based on the current page
			let start = this.pageSize*this.page
			let end = Math.min(start + this.pageSize, this.data.length)
			if (this.options.rowTemplate) {
				let rows = ''
				for (var i = start; i < end; i++) {
					rows += this.options.rowTemplate(this.data[i])
				}
				this.tbody.append(rows)
			} else {
				var frag = document.createDocumentFragment();
				for (var i = start; i < end; i++) {
					frag.appendChild(this.options.row(this.data[i]))
				}
				this.tbody[0].append(frag)
			}

			// re-render the pagination control so the numbers are updated
			this._renderPaginationControl()

			this.setupTableBody()

			// On first render, the thead is set to not displaye because
			// it is rendered first and the body might take a while to
			// render. All that's visible are loading dots. Make sure the
			// thead is visible now that we've renderd the body.
			this.$('thead').removeClass('is-not-displayed')
			this.toggleLoadingDots(false)
			this.isRenderingTable = false
		// safari needs an extra 10ms to trigger the loading dots properly #35987
		}, deviceType.safari ? 10 : 0)
	},

	/**
	 * Used to set the pagination control to a given page
	 * @param  {Number} page The page index the control is to be set to
	 */
	setPage(page) {
		// If we're already in the middle of rendering a page, don't try to set other
		// pages because this rendering can be quite expensive.
		if (this.isRenderingTable) {
			return
		}

		if (page == 'back') {
			page = Math.max(this.page - 1, 0)
		}

		if (page == 'forward') {
			page = Math.min(this.page + 1, this.pages.length - 1)
		}

		this.pages[this.page].selected = false // unselect prev the prev page
		this.page = page
		this.pages[this.page].selected = true // select new page
		this._renderTableBody()
	},

	/**
	 * Puts together the pagination ui indicating the selected/unselected pages.
	 * @param {Array} options.pageArr An array of numbers which are to be fed into the
	 * @param {Boolean} options.startEllipsis True if there should be a starting ellipsis
	 * @param {Boolean} options.endEllipsis True if there should be an ending ellipsis
	 */
	_setupControl(options = {}) {
		this.pages = new Array(Math.ceil(this.data.length / this.pageSize))
		this.page = this.page || this.options.page || 0

		let start = _.head(options.pageArr)-1
		let end = _.last(options.pageArr)-1
		let startEllipsis = options.startEllipsis && _.head(options.pageArr) > 1 ? 1 : null
		let endEllipsis = options.endEllipsis
			&& _.last(options.pageArr) < this.pages.length ? this.pages.length - 2 : null

		for (var i = 0; i < this.pages.length; i++) {
			let page = {
				id: i,
				label: i + 1
			}
			// The first/last pages are always shown
			if (i == 0 || i == this.pages.length-1) {
				this.pages[i] = page
			// Append any ellipsis as needed
			} else if (i == startEllipsis || i == endEllipsis) {
				this.pages[i] = _.extend({ isEllipsis: true}, page)
			// Insert the page numbers according to the proper range defined by start/end
			} else {
				let hidden = start > i || end < i
				this.pages[i] = _.extend({ hidden }, page)
			}

			if (this.page == i) {
				this.pages[i].selected = true
			}
		}
	},

	/**
	 * Returns true if the string of page numbers (numWidth) will fit into the
	 * available space for the control, else return false.
	 */
	_fitsAvailableSpace(numWidth) {
		return numWidth < this._getAvailableWidth()
	},

	/**
	 * Returns the pixel width of a number when given how many (n) digits
	 * where are in that number.
	 */
	_getNumerWidth(n) {
		return digitPadding*2 + n.toString().length * digitWidth
	},

	/**
	 * If a page index is given, return it's outer width if it were to appear
	 * in the UI (because it will be slightly wider given a heavier font weight
	 * than 'pageWidth')
	 */
	_calcNumWidth(i) {
		let sel = this.$(`[data-page-id=${i}].is-selected`)
		if (sel.length > 0) {
			return sel.outerWidth()
		} else {
			return this._getNumerWidth(i)
		}
	},

	/**
	 * Returns the starting width of the pagination control, which comes out to
	 * the starting available width minus the elliipses, first, last page, etc.
	 */
	_getStartingWidth(options = { ellipsis: 1 }) {
		let selectedWidth = this.$('.is-selected').outerWidth()
		let ellipsisWidth = this._getAvailableWidth() < this.originaWidth ? pageWidth*options.ellipsis : 0
		return selectedWidth + ellipsisWidth + this.largestNumWidth
	},

	/**
	 * Creates a pageArr of numbers starting from the beginning to however
	 * many numbers can fit in the available width for the component.
	 */
	_writeFromStart(pageArr) {
		let startingWidth = this._getStartingWidth()
		let i = 1

		while (this._fitsAvailableSpace(startingWidth) && i <= this.pages.length) {
			startingWidth += this._calcNumWidth(i)
			pageArr.push(i)
			i++
		}

		this._setupControl({ pageArr, endEllipsis: true })
	},

	/**
	 * Creates a pageArr of numbers starting from the end to however
	 * many numbers can fit in the available width for the component.
	 */
	_writeFromEnd(pageArr) {
		let startingWidth = this._getStartingWidth()
		let i = _.last(this.pages).id

		while (this._fitsAvailableSpace(startingWidth) && i >= 1) {
			startingWidth += this._calcNumWidth(i)
			pageArr.unshift(i)
			i--
		}

		this._setupControl({ pageArr, startEllipsis: true })
	},

	/**
	 * Returns the available space to use for rendering the pagination pages + ellipsis
	 */
	_getAvailableWidth() {
		return this.$el.width() - pageWidth*2 // compensate for arrows
	},

	resize() {
		let pageArr = []

		// If we have enough space to show the whole pagination control, we don't
		// need to readjust the page layout, unless there's an ellipsis left over which
		// needs to be removed.
		if (this._getAvailableWidth() >= this.originaWidth && this.$('.ellipsis').length == 0) {
			return
		}

		// If we're on the first page, set the pagination control up like so
		// < 1 2 3 4 5 6 7 ... 999 >
		if (this.page+1 <= 10) {
			this._writeFromStart(pageArr)
		// If we're on the last page, set the pagination control up like so
		// < 1 ... 995 996 997 998 999 >
		} else if (this.page+1 == this.pages.length) {
			this._writeFromEnd(pageArr)
		// If we're somewhere in the middle page-wise set the pagination control
		// up like so < 1 ... 500 501 [502] 503 504 ... 999 >
		} else {
			let startingWidth = this._getStartingWidth({ ellipsis: 2 }) + pageWidth
			let l = r = 1
			let op = '+'
			let p = this.page+1

			let canAddRight = () => {
				return op == '+' && p + r <= this.pages.length
			}

			let canAddLeft = () => {
				return p - l >= 1
			}

			while (this._fitsAvailableSpace(startingWidth) && (canAddRight() || canAddLeft())) {
				if (canAddRight()) {
					if (p + r == 1) {
						this._writeFromStart(pageArr)
						return
					}

					startingWidth += this._getNumerWidth(p+r)
					pageArr.push(p + r)
					op = '-'
					r++
				} else if (canAddLeft()) {
					if (p - l == this.pages.length) {
						this._writeFromEnd(pageArr)
						return
					}

					startingWidth += this._getNumerWidth(p-l)
					pageArr.unshift(p - l)
					op = '+'
					l++
				}
			}

			let startEllipsis = endEllipsis = true
			if (!this._fitsAvailableSpace(startingWidth)) {
				// If the selected page is right next to a right or left ellipsis,
				// we need to be sure to begin dropping pages from the right/left
				// of the pageArr in order, as well as show/hide ellipsis to compensate.
				//
				// To do that, see if the selected page is up against the left/right ellipsis.
				// If there's only one more page to go (left-wise or right-size), show it. If that doesn't
				// solve the problem of the component not fitting, start taking
				// numbers from the other side, replacing them with an ellipsis.
				if (this._selectedHasLeftEllipsisSibling()) {
					let removedLeftEllipsis
					while (!this._fitsAvailableSpace(startingWidth)) {
						if (this.page - 1 > 1 || removedLeftEllipsis) {
							let removed = pageArr.pop()
							startingWidth-= this._getNumerWidth(removed) // Take away removed page width
							startEllipsis = true
						} else {
							let leftPage = p-l
							startEllipsis = false
							pageArr.unshift(leftPage)
							removedLeftEllipsis = true
							startingWidth-=pageWidth // account for the ellipsis removal
							startingWidth+=this._getNumerWidth(leftPage) // Add newly added page
						}
						endEllipsis = true
					}
				} else if (this._selectedHasRightEllipsisSibling()) {
					while (!this._fitsAvailableSpace(startingWidth)) {
						if (this.pages.length-1 - this.page > 1 || removedRightEllipsis) {
							let removed = pageArr.shift()
							startingWidth-= this._getNumerWidth(removed) // Take away removed page width
							endEllipsis = true
						} else {
							let rightPage = p+r
							endEllipsis = false
							pageArr.unshift(rightPage)
							removedRightEllipsis = true
							startingWidth-=pageWidth // account for the ellipsis removal
							startingWidth+=this._getNumerWidth(rightPage) // Add newly added page
						}
						startEllipsis = true
					}
				}
			}

			this._setupControl({ pageArr, startEllipsis, endEllipsis })
		}

		this._renderTableBody()
	}
})

export default Pagination

Pagination.__filename__ = 'components/pagination.js'
